import { Component, Inject, Injector, Input, Optional } from '@angular/core';
import { EditorHandler } from '../../interface/handler/editor-handler';
import { IdeCodeViewConfig, IDE_CODE_VIEW_CONFIG } from '../../config/global.config';
import { EventBusHandler } from '../../event-bus/handler';
import { DEFAULT_PLUGINS_URL_MAP } from '../../config/plugins.config';
import { cloneDeep } from 'lodash-es';
import { ShortcutKeyDesc } from '../../interface/placeholder';
import { LocaleService } from '@farris/ui-locale';

interface EditorPanel {
  /** 对应的文件路径 */
  path: string;
  /** 插件的路径 */
  url: string;
}



/**
 * 中心编辑器组件
 * @remarks
 * 该组件主要负责打开、显示或关闭编辑器面板，向外界屏蔽编辑器的存在
 */
@Component({
  selector: 'webide-center-panel',
  templateUrl: './center-panel.component.html'
})
export class CenterPanelComponent implements EditorHandler {

  @Input() shortcutsList: ShortcutKeyDesc[] = null;

  get shortcuts(): ShortcutKeyDesc[] {
    return this.shortcutsList || this.getDefaultShortcuts();
  }

  /** 代码编辑器插件数据 */
  editorPanels: EditorPanel[] = [];

  /** 当前显示的文件路径 */
  activePath: string = null;

  private eventBusId: string;

  constructor(
    private injector: Injector,  // 防止依赖变更导致需要重新打包
    private eventBusHandler: EventBusHandler,
    @Optional() @Inject(IDE_CODE_VIEW_CONFIG) private config?: IdeCodeViewConfig
  ) {
    this.eventBusId = this.eventBusHandler.eventBusId;
  }
  /**
   * 默认显示的快捷键列表
   */
  private getDefaultShortcuts(): ShortcutKeyDesc[] {
    const localeService = this.injector.get(LocaleService);

    return [{
      name: localeService && localeService.getValue('codeViewDesigner.saveCurrentTab') || '保存当前标签页',
      keys: ['Ctrl', 'S'],
      title: localeService && localeService.getValue('codeViewDesigner.saveCurrentTabTip') || '无论是否有修改，都重新保存当前标签页'
    }, {
      name: localeService && localeService.getValue('codeViewDesigner.find') || '查找',
      keys: ['Ctrl', 'F'],
      title: localeService && localeService.getValue('codeViewDesigner.find') || '查找'
    }, {
      name: localeService && localeService.getValue('codeViewDesigner.collpaseOrExpandNavPanel') || '收起/展开导航面板',
      keys: ['Ctrl', 'B'],
      title: localeService && localeService.getValue('codeViewDesigner.collpaseOrExpandNavPanelTip') || '收起/展开左侧的导航面板'
    }, {
      name: localeService && localeService.getValue('codeViewDesigner.openCommandPanel') || '编辑器命令面板',
      keys: ['F1'],
      title: localeService && localeService.getValue('codeViewDesigner.openCommandPanelTip') || '打开代码编辑器的命令面板（需要焦点在代码编辑器时触发）'
    }, {
      name: localeService && localeService.getValue('codeViewDesigner.formatDocument') || '格式化文档',
      keys: ['Shift', 'Alt', 'F'],
      title: localeService && localeService.getValue('codeViewDesigner.formatDocumentTip') || '格式化文档（需要焦点在代码编辑器时触发）'
    }];

  }

  /**
   * 获取代码编辑器面板页面的Url
   * @param path 文件路径
   * @param param 额外的查询参数
   * @returns 编辑面板路径
   */
  private getCodeEditorPanelUrl(path: string, param?: { [key: string]: string }): string {
    const pluginUrl = this.getPluginUrl(path);
    if (!pluginUrl) {
      return null;
    }
    let url = `${pluginUrl}/index.html?id=${path}&eventBusId=${this.eventBusId}&fromCodeView=true`;
    if (!!param) {
      for (let key in param) {
        if (key === 'id') {
          continue;
        }
        url += `&${key}=${param[key]}`;
      }
    }
    return url;
  }

  /**
   * 获取打开该文件所需的插件的地址
   * @param path 文件路径
   * @returns 插件地址
   */
  private getPluginUrl(path: string): string {
    // 合并默认配置与自定义配置
    const config = cloneDeep(DEFAULT_PLUGINS_URL_MAP) || {};
    const customConfig = this.config && this.config.fileSuffix2PluginsUrlMap || {};
    Object.assign(config, customConfig);
    // 返回符合要求的路径配置
    for (const suffixKey in config) {
      if (path.endsWith(suffixKey)) {
        return config[suffixKey];
      }
    }
    return null;
  }

  show(path: string): boolean {
    const targetPanel = this.editorPanels.find(panel => panel.path === path);
    if (!targetPanel) {
      return false;
    }
    this.activePath = path;
    return true;
  }

  open(path: string, param?: { [key: string]: string }): boolean {
    if (this.show(path)) {
      return;
    }
    const newPanel: EditorPanel = { path, url: this.getCodeEditorPanelUrl(path, param) };
    if (!newPanel.url) {
      return false;
    }
    this.editorPanels.push(newPanel);
    this.show(path);
    return true;
  }

  close(path: string) {
    const idx = this.editorPanels.findIndex(panel => panel.path === path);
    if (idx >= 0) {
      if (this.activePath === path) {
        this.activePath = null;
      }
      this.editorPanels.splice(idx, 1);
    }
  }

}
